import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search } from "lucide-react";

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [guests, setGuests] = useState("any");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api
      .get("/properties")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const locations = useMemo(
    () => Array.from(new Set(items.map((i) => i.location))),
    [items],
  );

  const [loc, setLoc] = useState("any");

  const filtered = useMemo(() => {
    let list = [...items];
    const query = q.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(query) ||
          i.location.toLowerCase().includes(query) ||
          (i.tagline || "").toLowerCase().includes(query),
      );
    }
    if (loc !== "any") list = list.filter((i) => i.location === loc);
    if (guests !== "any") {
      const n = parseInt(guests, 10);
      list = list.filter((i) => i.max_guests >= n);
    }
    if (sort === "price-asc")
      list.sort((a, b) => a.price_per_night - b.price_per_night);
    else if (sort === "price-desc")
      list.sort((a, b) => b.price_per_night - a.price_per_night);
    return list;
  }, [items, q, loc, guests, sort]);

  return (
    <div data-testid="properties-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="overline mb-4">The Collection</p>
        <h1 className="font-serif-luxe text-6xl md:text-7xl leading-none tracking-tighter">
          Every home,{" "}
          <span className="italic text-[#D4AF37]">personally chosen</span>.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#F5F5DC]/80 leading-relaxed">
          Browse the collection. Filter by location or party size. When you find
          the one, reach out directly — I answer every inquiry myself.
        </p>

        {/* Filter bar */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 items-center border-t border-b border-[#F5F5DC]/10 py-6">
          <div className="md:col-span-5 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"
            />
            <Input
              data-testid="filter-search"
              placeholder="Search by name or location…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 bg-transparent border-[#F5F5DC]/10 focus:border-[#D4AF37] text-[#F5F5DC] placeholder:text-[#737373] h-11"
            />
          </div>
          <div className="md:col-span-2">
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger
                data-testid="filter-location"
                className="bg-transparent border-[#F5F5DC]/10 h-11 text-[#F5F5DC]"
              >
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any location</SelectItem>
                {locations.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={guests} onValueChange={setGuests}>
              <SelectTrigger
                data-testid="filter-guests"
                className="bg-transparent border-[#F5F5DC]/10 h-11 text-[#F5F5DC]"
              >
                <SelectValue placeholder="Guests" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any guests</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="6">6+</SelectItem>
                <SelectItem value="8">8+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                data-testid="filter-sort"
                className="bg-transparent border-[#F5F5DC]/10 h-11 text-[#F5F5DC]"
              >
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="price-asc">Price: low → high</SelectItem>
                <SelectItem value="price-desc">Price: high → low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-[#A3A3A3] mt-12">Loading the collection…</p>
        ) : filtered.length === 0 ? (
          <div
            data-testid="properties-empty"
            className="mt-24 text-center text-[#A3A3A3]"
          >
            <p className="font-serif-luxe text-3xl mb-3 text-[#F5F5DC]">
              No stays match those filters.
            </p>
            <p>Try widening your search — the collection is small on purpose.</p>
          </div>
        ) : (
          <div
            data-testid="properties-grid"
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filtered.map((p, i) => (
              <PropertyCard property={p} index={i} key={p.id} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
